
import { Contract } from './types';

// Function to generate sample contract content for preview and download
export const generateContractContent = (template: Contract): string => {
  const contents: Record<number, string> = {
    1: `RECORDING CONTRACT

AGREEMENT made this [DAY] day of [MONTH], [YEAR], by and between [RECORD LABEL NAME], with a principal address at [ADDRESS] (hereinafter referred to as "Label"), and [ARTIST NAME], with a principal address at [ADDRESS] (hereinafter referred to as "Artist").

WITNESSETH:

WHEREAS, Artist is a musician and performer, and 

WHEREAS, Label is engaged in the business of recording and distributing sound recordings, and

WHEREAS, Label desires to engage Artist to perform for the purpose of making recordings upon the terms and conditions hereinafter set forth, and

WHEREAS, Artist desires to enter into such an engagement;

NOW, THEREFORE, in consideration of the mutual covenants and promises herein contained, it is agreed as follows:

1. TERM
   1.1 The initial term of this Agreement shall be for a period of [NUMBER] years commencing on the date of execution of this Agreement.
   1.2 Label shall have the option to extend this Agreement for [NUMBER] additional periods of one year each, upon the same terms and conditions, except as modified herein.

2. RECORDING COMMITMENT
   2.1 During the term hereof, Artist agrees to record for Label a minimum of [NUMBER] master recordings.
   2.2 Each master recording shall be of technical and commercial quality satisfactory to Label.
   
3. COMPENSATION
   3.1 Label agrees to pay Artist the following royalties:
       a) [PERCENTAGE]% of the suggested retail price for all recordings sold in the United States.
       b) [PERCENTAGE]% of the suggested retail price for all recordings sold outside the United States.
       c) [PERCENTAGE]% of Label's net receipts from digital downloads.
       d) [PERCENTAGE]% of Label's net receipts from streaming services.

4. ADVANCES
   4.1 Label agrees to pay Artist a non-returnable but recoupable advance of [AMOUNT] upon execution of this Agreement.
   4.2 For each additional option period, Label shall pay Artist a non-returnable but recoupable advance of [AMOUNT].

5. COPYRIGHT
   5.1 All master recordings made hereunder and all matrices and phonograph records manufactured therefrom, together with the performances embodied therein, shall be entirely Label's property, free from any claims whatsoever by Artist or any person deriving any rights from Artist.

6. TERMINATION
   6.1 This Agreement may be terminated by either party upon material breach by the other party, which breach remains uncured for a period of thirty (30) days following written notice thereof.
   
IN WITNESS WHEREOF, the parties hereto have executed this Agreement as of the day and year first above written.

[RECORD LABEL NAME]
By: ____________________________

[ARTIST NAME]
By: ____________________________`,
    2: `MUSIC PRODUCER AGREEMENT

AGREEMENT made this [DAY] day of [MONTH], [YEAR], by and between [ARTIST NAME], with a principal address at [ADDRESS] (hereinafter referred to as "Artist"), and [PRODUCER NAME], with a principal address at [ADDRESS] (hereinafter referred to as "Producer").

WITNESSETH:

WHEREAS, Artist desires to retain Producer to produce certain master recordings, and

WHEREAS, Producer desires to produce such master recordings for Artist;

NOW, THEREFORE, in consideration of the mutual covenants and promises herein contained, it is agreed as follows:

1. ENGAGEMENT
   1.1 Artist hereby engages Producer to produce [NUMBER] master recordings (the "Masters") embodying performances by Artist.
   1.2 Producer accepts such engagement and agrees to perform all duties customarily required of a producer in the music industry.

2. TERM
   2.1 The term of this Agreement shall commence on the date of execution and shall continue until completion of Producer's services hereunder.
   
3. COMPENSATION
   3.1 Artist agrees to pay Producer a non-returnable advance of [AMOUNT] upon execution of this Agreement.
   3.2 Artist agrees to pay Producer a royalty of [PERCENTAGE]% of the suggested retail price for all recordings sold in the United States.
   3.3 Artist agrees to pay Producer [PERCENTAGE]% of Artist's net receipts from digital downloads and streaming services.

4. CREDIT
   4.1 Producer shall receive appropriate credit on all copies of the Masters, in substantially the following form: "Produced by [PRODUCER NAME]".

5. RECORDING COSTS
   5.1 Artist shall be responsible for all recording costs, including but not limited to studio time, musicians, engineers, and equipment rental.
   
6. RIGHTS
   6.1 Producer acknowledges that Artist shall be the exclusive owner of all masters produced hereunder, subject to Producer's royalty interest.

IN WITNESS WHEREOF, the parties hereto have executed this Agreement as of the day and year first above written.

[ARTIST NAME]
By: ____________________________

[PRODUCER NAME]
By: ____________________________`,
    3: `SONGWRITER COLLABORATION AGREEMENT

AGREEMENT made this [DAY] day of [MONTH], [YEAR], by and between [WRITER 1 NAME], with a principal address at [ADDRESS] (hereinafter referred to as "Writer 1"), and [WRITER 2 NAME], with a principal address at [ADDRESS] (hereinafter referred to as "Writer 2").

WITNESSETH:

WHEREAS, Writer 1 and Writer 2 (collectively, the "Writers") wish to collaborate on musical compositions;

NOW, THEREFORE, in consideration of the mutual covenants and promises herein contained, it is agreed as follows:

1. COLLABORATION
   1.1 The Writers agree to collaborate in the writing of musical compositions (hereinafter referred to as the "Compositions").
   
2. OWNERSHIP
   2.1 The Writers shall own the Compositions in the following percentages:
       Writer 1: [PERCENTAGE]%
       Writer 2: [PERCENTAGE]%
   2.2 These percentages apply to all elements of the Compositions, including music, lyrics, and arrangements.

3. ADMINISTRATION
   3.1 The Writers agree that the Compositions shall be administered by [ADMINISTRATOR NAME], under a separate administration agreement.
   3.2 In the absence of an administrator, the Writers shall jointly administer the Compositions.

4. CREDIT
   4.1 On all publications of the Compositions, the Writers shall receive credit in the following form: "Written by [WRITER 1 NAME] and [WRITER 2 NAME]".

5. EXPLOITATION
   5.1 Neither Writer shall license or assign rights in the Compositions without the prior written consent of the other Writer, which consent shall not be unreasonably withheld.
   
6. REVENUE
   6.1 All revenue derived from the Compositions shall be divided between the Writers according to the ownership percentages set forth in Section 2.1.

7. TERM
   7.1 This Agreement shall remain in effect for the full term of copyright in the Compositions, including any renewals, extensions, or reversions.

IN WITNESS WHEREOF, the parties hereto have executed this Agreement as of the day and year first above written.

[WRITER 1 NAME]
By: ____________________________

[WRITER 2 NAME]
By: ____________________________`,
    4: `SYNC LICENSE AGREEMENT

AGREEMENT made this [DAY] day of [MONTH], [YEAR], by and between [COPYRIGHT OWNER NAME], with a principal address at [ADDRESS] (hereinafter referred to as "Licensor"), and [LICENSEE NAME], with a principal address at [ADDRESS] (hereinafter referred to as "Licensee").

WITNESSETH:

WHEREAS, Licensor owns and controls certain rights in and to the musical composition entitled "[COMPOSITION TITLE]" (hereinafter referred to as the "Composition") and the master recording of the Composition (hereinafter referred to as the "Master");

WHEREAS, Licensee desires to use the Composition and Master in connection with the [PRODUCTION TITLE] (hereinafter referred to as the "Production");

NOW, THEREFORE, in consideration of the mutual covenants and promises herein contained, it is agreed as follows:

1. GRANT OF RIGHTS
   1.1 Licensor hereby grants to Licensee the non-exclusive right to synchronize the Composition and Master with the Production and to exhibit, distribute, and exploit the Production containing the Composition and Master in all media now known or hereafter devised, throughout the Territory, in perpetuity.
   
2. TERRITORY
   2.1 The territory covered by this Agreement (the "Territory") shall be the world.
   
3. FEE
   3.1 In consideration for the rights granted herein, Licensee shall pay Licensor a one-time synchronization fee of [AMOUNT].
   
4. CREDIT
   4.1 Licensee shall give Licensor credit in the Production as follows: "[COMPOSITION TITLE]" written by [SONGWRITER NAME], performed by [ARTIST NAME].
   
5. WARRANTIES
   5.1 Licensor warrants and represents that Licensor has the right and authority to enter into this Agreement and to grant the rights granted herein.
   
6. TERMINATION
   6.1 This Agreement shall terminate if Licensee fails to pay the Fee set forth in Section 3.1 within thirty (30) days of execution of this Agreement.

IN WITNESS WHEREOF, the parties hereto have executed this Agreement as of the day and year first above written.

[LICENSOR NAME]
By: ____________________________

[LICENSEE NAME]
By: ____________________________`,
    5: `BAND PARTNERSHIP AGREEMENT

AGREEMENT made this [DAY] day of [MONTH], [YEAR], by and between [MEMBER 1 NAME], [MEMBER 2 NAME], [MEMBER 3 NAME], and [MEMBER 4 NAME] (collectively, the "Partners" or "Members").

WITNESSETH:

WHEREAS, the Partners wish to form a band partnership (the "Band") for the purpose of performing and recording music;

NOW, THEREFORE, in consideration of the mutual covenants and promises herein contained, it is agreed as follows:

1. NAME
   1.1 The name of the Band shall be "[BAND NAME]".
   1.2 The Band name shall be owned equally by all Partners.
   
2. PARTNERSHIP SHARES
   2.1 Each Partner shall have an equal [PERCENTAGE]% interest in the Band.
   
3. MANAGEMENT AND CONTROL
   3.1 All major decisions affecting the Band shall require unanimous consent of all Partners.
   3.2 Major decisions include, but are not limited to:
       a) Signing recording or publishing contracts
       b) Hiring or firing of managers, agents, or attorneys
       c) Expenditures exceeding [AMOUNT]
       d) Addition or removal of Band members
       
4. BAND PROPERTY
   4.1 All property acquired with Band funds or for Band purposes shall be owned by the Partners in proportion to their partnership shares.
   
5. SONGWRITING AND PUBLISHING
   5.1 Unless otherwise agreed in writing, songs written by Band members shall be owned by the actual writers, not the Band as a whole.
   5.2 Publishing income shall be divided among the writers according to their contribution.
   
6. RECORDINGS
   6.1 Income from recordings shall be divided equally among all Partners after recoupment of recording costs.
   
7. LIVE PERFORMANCES
   7.1 Income from live performances shall be divided equally among all Partners after payment of expenses.
   
8. DEPARTURE OF MEMBERS
   8.1 Any Partner may withdraw from the Band upon giving [NUMBER] days written notice to the other Partners.
   8.2 A departing Partner shall retain their rights in songs written and recordings made during their membership.
   8.3 A departing Partner shall have no right to use the Band name.

IN WITNESS WHEREOF, the parties hereto have executed this Agreement as of the day and year first above written.

[MEMBER 1 NAME]
By: ____________________________

[MEMBER 2 NAME]
By: ____________________________

[MEMBER 3 NAME]
By: ____________________________

[MEMBER 4 NAME]
By: ____________________________`,
    6: `MUSIC PUBLISHING AGREEMENT

AGREEMENT made this [DAY] day of [MONTH], [YEAR], by and between [SONGWRITER NAME], with a principal address at [ADDRESS] (hereinafter referred to as "Songwriter"), and [PUBLISHER NAME], with a principal address at [ADDRESS] (hereinafter referred to as "Publisher").

WITNESSETH:

WHEREAS, Songwriter has composed, and may hereafter compose, certain musical compositions;

WHEREAS, Publisher desires to acquire certain rights in and to such compositions;

NOW, THEREFORE, in consideration of the mutual covenants and promises herein contained, it is agreed as follows:

1. GRANT OF RIGHTS
   1.1 Songwriter hereby irrevocably assigns to Publisher all right, title, and interest in and to the musical compositions set forth on Schedule A attached hereto and made a part hereof, and any and all musical compositions written or co-written by Songwriter during the Term (collectively, the "Compositions"), together with all copyrights therein and all renewals and extensions thereof.
   
2. TERM
   2.1 The term of this Agreement (the "Term") shall be for [NUMBER] years, commencing on the date of execution of this Agreement.
   
3. ADVANCES
   3.1 Publisher shall pay Songwriter a non-returnable but recoupable advance of [AMOUNT] upon execution of this Agreement.
   
4. ROYALTIES
   4.1 Publisher shall pay Songwriter the following royalties:
       a) [PERCENTAGE]% of all mechanical royalties received by Publisher
       b) [PERCENTAGE]% of all performance royalties received by Publisher
       c) [PERCENTAGE]% of all synchronization fees received by Publisher
       d) [PERCENTAGE]% of all other income received by Publisher from exploitation of the Compositions
   
5. ACCOUNTING
   5.1 Publisher shall render semi-annual statements to Songwriter within ninety (90) days after the end of each calendar half-year, together with payment of all royalties earned during such period.
   
6. SONGWRITER'S WARRANTIES
   6.1 Songwriter warrants and represents that:
       a) Songwriter is the sole author of the Compositions
       b) The Compositions are original
       c) The Compositions do not infringe on any copyrights or other rights of any third parties
       d) Songwriter has the full right and authority to enter into this Agreement
   
7. REVERSION
   7.1 If the Publisher fails to secure a commercial release or use of a Composition within a period of [NUMBER] years from the date of its delivery to Publisher, then upon written notice from Songwriter, all rights in such Composition shall revert to Songwriter.

IN WITNESS WHEREOF, the parties hereto have executed this Agreement as of the day and year first above written.

[SONGWRITER NAME]
By: ____________________________

[PUBLISHER NAME]
By: ____________________________`
  };
  
  return contents[template.id] || 
    `SAMPLE CONTRACT: ${template.title.toUpperCase()}

This is a sample template for a ${template.title.toLowerCase()}. In a real-world scenario, this contract would contain detailed clauses covering:

1. Parties involved
2. Term of agreement
3. Scope of work/services
4. Compensation and payment terms
5. Rights and ownership
6. Warranties and representations
7. Termination clauses
8. Governing law

IMPORTANT NOTICE: This is provided as a template only and should be reviewed by a qualified attorney before use. This template is compatible with ${template.compatibleWith} and was last updated ${template.lastUpdated}.

Please download the full template for a complete version of this contract.`;
};
